import { ChevronDown, Search } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import type { CategoryKey } from '@worldcoo/shared/core';
import { CATEGORY_ORDER, useCategoryLabels } from '@/lib/category-labels';
import type { FilterOption, NgoFilters } from './use-partner-ngos';

/**
 * "No filter" needs a value a `<select>` can carry, and an empty string is indistinguishable from an
 * option whose value happens to be blank. A reserved word instead, mapped back to `null` the moment
 * it leaves the control — nothing downstream of these dropdowns should know the sentinel exists.
 */
const ANY = '__any__';

/** The hand-off's search field: white, placeholder left, glyph right. */
function NgoSearch(props: { value: string; onChange: (value: string) => void }) {
	const { t } = useLingui();
	return (
		<div className="relative w-full lg:w-[396px]">
			<input
				type="search"
				value={props.value}
				onChange={(event) => props.onChange(event.target.value)}
				placeholder={t`Search NGOs...`}
				aria-label={t`Search NGOs by name`}
				className="text-ink placeholder:text-ink/60 focus-visible:ring-ink w-full rounded-lg bg-white py-3 pr-11 pl-4 text-base tracking-[-0.02em] focus-visible:ring-2 focus-visible:outline-none [&::-webkit-search-cancel-button]:appearance-none"
			/>
			<Search aria-hidden className="text-ink pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2" />
		</div>
	);
}

/**
 * A filter dropdown, as a NATIVE `<select>`.
 *
 * Not the shared shadcn `Select`, and not the UI kit's `NativeSelect` either: the first is a Radix
 * listbox themed for the product UI, and the second hardcodes the app's `border-input` / muted
 * palette and a fixed 32px height. This is a bare `<select>` wearing the marketing site's outlined
 * pill — `appearance-none` plus the hand-off's own chevron — so the options list is the platform's,
 * which is what makes it behave properly on a phone.
 */
function FilterSelect(props: {
	label: string;
	value: string | null;
	options: FilterOption[];
	onChange: (value: string | null) => void;
	className?: string;
}) {
	// A filter with nothing to choose between is noise — the roster has one country, or none.
	if (props.options.length < 2) return null;

	return (
		<div className={cn('relative w-full lg:w-fit', props.className)}>
			<select
				aria-label={props.label}
				value={props.value ?? ANY}
				onChange={(event) => props.onChange(event.target.value === ANY ? null : event.target.value)}
				className="border-ink text-ink focus-visible:ring-ink w-full cursor-pointer appearance-none rounded-lg border bg-transparent py-3 pr-11 pl-6 text-base leading-[1.5] focus-visible:ring-2 focus-visible:outline-none"
			>
				{/* The "no filter" row is labelled with the filter's OWN name, because a native select's
				    trigger always shows the selected option: with "Todas las categorías" in that slot the
				    resting control read as a value rather than as a field, and truncated at mobile width.
				    The hand-off shows a bare "Categoría" until something is picked, which is this. */}
				<option value={ANY}>{props.label}</option>
				{props.options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
			<ChevronDown
				aria-hidden
				className="text-ink pointer-events-none absolute top-1/2 right-5 size-4 -translate-y-1/2"
			/>
		</div>
	);
}

/** Search on the left, the three dropdowns on the right — the hand-off's toolbar above the rows. */
export function NgoFiltersBar(props: {
	filters: NgoFilters;
	onChange: (filters: NgoFilters) => void;
	countryOptions: FilterOption[];
	regionOptions: FilterOption[];
}) {
	const { t } = useLingui();
	const categoryLabels = useCategoryLabels();
	const set = <K extends keyof NgoFilters>(key: K, value: NgoFilters[K]) =>
		props.onChange({ ...props.filters, [key]: value });

	return (
		<div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
			<NgoSearch value={props.filters.query} onChange={(value) => set('query', value)} />

			{/* The mobile frame lays these out as País | Región on one line with Categoría spanning
			    underneath, so it is a two-column grid that collapses to a row above `lg` — leaving them to
			    `flex-wrap` gives the same result only by accident, and breaks on a long country name. */}
			<div className="grid grid-cols-2 gap-4 lg:flex lg:gap-4">
				<FilterSelect
					label={t`Country`}
					value={props.filters.country}
					options={props.countryOptions}
					onChange={(value) => set('country', value)}
				/>
				<FilterSelect
					label={t`Region`}
					value={props.filters.region}
					options={props.regionOptions}
					onChange={(value) => set('region', value)}
				/>
				<FilterSelect
					label={t`Category`}
					value={props.filters.category}
					options={CATEGORY_ORDER.map((key) => ({ value: key, label: categoryLabels[key] }))}
					onChange={(value) => set('category', value as CategoryKey | null)}
					className="col-span-2 lg:col-span-1"
				/>
			</div>
		</div>
	);
}
